import { format, parseISO } from "date-fns";
import { SESSION_STATUS_LABELS_PT, TREATMENT_STATUS_LABELS_PT } from "../../../dashboard-framework/describeWidgetLogic";
import { formatEuros } from "../../../dashboard-framework/format";
import { DrillDownColumn } from "../../../dashboard-framework/types";

// Colunas default (pt-PT) por entidade para as modais de drill-down — campos sempre enumerados,
// nunca wildcards. Cada dashboard pode passar colunas próprias quando precisar de mais contexto.

export const formatDrillDate = (value: string | null | undefined) => (value ? format(parseISO(value), "dd/MM/yyyy") : null);
export const formatDrillDateTime = (value: string | null | undefined) => (value ? format(parseISO(value), "dd/MM/yyyy HH:mm") : null);
export const formatDrillEuros = formatEuros;

type PersonUser = { first_name?: string | null; last_name?: string | null } | null | undefined;
const personName = (user: PersonUser) => {
    if (!user) return null;
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    return name || null;
};

const sessionClientName = (session: any) => personName(session.treatment?.client?.user_id) || personName(session.client?.user_id);

export const SESSIONS_DRILL_FIELDS =
    "id,start_date,status,service.name,clinic_id.name,treatment.id,treatment.client.user_id.first_name,treatment.client.user_id.last_name,client.user_id.first_name,client.user_id.last_name";
export const sessionsDrillColumns: Array<DrillDownColumn> = [
    { key: "start_date", label: "Data", sortField: "start_date", valueFor: (session) => formatDrillDateTime(session.start_date) },
    { key: "client", label: "Cliente", valueFor: sessionClientName },
    { key: "service", label: "Serviço", sortField: "service.name", valueFor: (session) => session.service?.name ?? null },
    { key: "status", label: "Estado", sortField: "status", valueFor: (session) => SESSION_STATUS_LABELS_PT[session.status] || session.status },
    { key: "clinic", label: "Clínica", sortField: "clinic_id.name", valueFor: (session) => session.clinic_id?.name ?? null },
];
export const sessionRoute = (session: any) => (session.id ? `/sessions/${session.id}` : null);

export const TREATMENTS_DRILL_FIELDS = "id,date_of_start,treatment_status_new,client.user_id.first_name,client.user_id.last_name";
export const treatmentsDrillColumns: Array<DrillDownColumn> = [
    { key: "client", label: "Cliente", valueFor: (treatment) => personName(treatment.client?.user_id) },
    { key: "date_of_start", label: "Início", sortField: "date_of_start", valueFor: (treatment) => formatDrillDate(treatment.date_of_start) },
    {
        key: "treatment_status_new",
        label: "Estado",
        sortField: "treatment_status_new",
        valueFor: (treatment) => TREATMENT_STATUS_LABELS_PT[treatment.treatment_status_new] || treatment.treatment_status_new,
    },
];
export const treatmentRoute = (treatment: any) => (treatment.id ? `/treatments/${treatment.id}` : null);

export const customerSurveyRoute = (survey: any) => (survey.id ? `/customer_surveys/${survey.id}` : null);

export const LEADS_DRILL_FIELDS = "id,first_name,last_name,phone,date_created,client_type,status,channel,origin,utm_source,utm_campaign,clinic.name";
// Valores do campo leads.client_type → pt-PT (fallback quando o chipField do campo ainda não carregou).
const CLIENT_TYPE_FIELD_LABELS_PT: Record<string, string> = { child: "Criança", adult: "Adulto" };
// client_type e status são renderizados como chips com a cor/label das definições do campo Directus
// (o dashboard injeta o chipField via useFields — mesmo aspeto da grid).
export const leadsDrillColumns: Array<DrillDownColumn> = [
    { key: "name", label: "Nome", valueFor: (lead) => personName(lead) },
    { key: "date_created", label: "Data", sortField: "date_created", valueFor: (lead) => formatDrillDateTime(lead.date_created) },
    { key: "clinic", label: "Clínica", sortField: "clinic.name", valueFor: (lead) => lead.clinic?.name ?? null },
    { key: "client_type", label: "Tipo de cliente", sortField: "client_type", valueFor: (lead) => CLIENT_TYPE_FIELD_LABELS_PT[lead.client_type] || lead.client_type || null },
    { key: "status", label: "Estado", sortField: "status" },
    { key: "channel", label: "Canal", sortField: "channel" },
    { key: "origin", label: "Origem", sortField: "origin" },
    { key: "utm_source", label: "utm_source", sortField: "utm_source" },
    { key: "utm_campaign", label: "utm_campaign", sortField: "utm_campaign" },
];
export const leadRoute = (lead: any) => (lead.id ? `/leads/${lead.id}` : null);

export const BANK_TRANSACTIONS_DRILL_FIELDS = "id,transaction_date,document_date,description,amount,category.name";
export const bankTransactionsDrillColumns: Array<DrillDownColumn> = [
    {
        key: "transaction_date",
        label: "Data",
        sortField: "transaction_date",
        valueFor: (transaction) => formatDrillDate(transaction.transaction_date),
    },
    { key: "description", label: "Descrição", sortField: "description" },
    { key: "category", label: "Categoria", sortField: "category.name", valueFor: (transaction) => transaction.category?.name ?? null },
    {
        key: "amount",
        label: "Montante",
        isNumeric: true,
        sortField: "amount",
        valueFor: (transaction) => formatDrillEuros(Number(transaction.amount || 0)),
    },
];
export const bankTransactionRoute = (transaction: any) => (transaction.id ? `/bank_transactions/${transaction.id}` : null);

export const ORDERS_PAYMENTS_DRILL_FIELDS = "id,payment_date,amount,order,payment_method";
export const ordersPaymentsDrillColumns: Array<DrillDownColumn> = [
    { key: "payment_date", label: "Data", sortField: "payment_date", valueFor: (payment) => formatDrillDate(payment.payment_date) },
    { key: "payment_method", label: "Método", sortField: "payment_method" },
    { key: "order", label: "Encomenda", sortField: "order" },
    { key: "amount", label: "Montante", isNumeric: true, sortField: "amount", valueFor: (payment) => formatDrillEuros(Number(payment.amount || 0)) },
];
export const orderPaymentRoute = (payment: any) => (payment.order ? `/orders/${payment.order}` : null);

export const BANKS_DRILL_FIELDS = "id,name,balance,company";
export const banksDrillColumns: Array<DrillDownColumn> = [
    { key: "name", label: "Conta", sortField: "name" },
    { key: "company", label: "Empresa (id)", sortField: "company" },
    { key: "balance", label: "Saldo", isNumeric: true, sortField: "balance", valueFor: (bank) => formatDrillEuros(Number(bank.balance || 0)) },
];
